import React, { useEffect } from 'react';
import Header from '../../components/common/Header/Header';
import Footer from '../../components/common/Footer/Footer';
import Layout from '../../components/common/Layout/Layout';

const DisclaimerPage = () => {
    // Force page to start from top when component mounts
    useEffect(() => {
        // Scroll to top immediately (no smooth animation)
        window.scrollTo(0, 0);
        
        // Also set scroll restoration to manual to prevent browser from remembering position
        if ('scrollRestoration' in history) {
            history.scrollRestoration = 'manual';
        }
    }, []);

    const styles = {
        pageContainer: {
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#ffffff'
        },
        
        mainContent: {
            flex: 1,
            backgroundColor: '#ffffff',
            padding: '3rem 0'
        },

        container: {
            maxWidth: '1000px',
            margin: '0 auto',
            padding: '0 2rem',
            lineHeight: '1.6'
        },

        pageTitle: {
            fontSize: '2.5rem',
            fontWeight: '700',
            color: '#1a202c',
            marginBottom: '3rem',
            textAlign: 'center',
            borderBottom: '3px solid #22c55e',
            paddingBottom: '1rem'
        },

        sectionTitle: {
            fontSize: '1.3rem',
            fontWeight: '700',
            color: '#1a202c',
            marginTop: '2.5rem',
            marginBottom: '1rem'
        },

        firstSectionTitle: {
            fontSize: '1.3rem',
            fontWeight: '700',
            color: '#1a202c',
            marginTop: '0',
            marginBottom: '1rem'
        },

        paragraph: {
            fontSize: '1rem',
            color: '#4a5568',
            lineHeight: '1.7',
            marginBottom: '1.5rem',
            textAlign: 'justify'
        },

        warningBox: {
            backgroundColor: '#fef2f2',
            border: '2px solid #dc2626',
            borderRadius: '8px',
            padding: '1.5rem',
            margin: '2rem 0',
            fontSize: '1.1rem',
            fontWeight: '600',
            color: '#dc2626',
            textAlign: 'center'
        },

        registrationBox: {
            backgroundColor: '#f0f9ff',
            border: '2px solid #0369a1',
            borderRadius: '8px',
            padding: '1.5rem',
            margin: '2rem 0',
            fontSize: '1rem',
            color: '#0369a1'
        },

        content: {
            fontSize: '1rem',
            color: '#2d3748',
            lineHeight: '1.7'
        }
    };

    return (
        <>
            <style>{`
                @media (max-width: 768px) {
                    .container {
                        padding: 0 1rem !important;
                    }
                    
                    .page-title {
                        font-size: 2rem !important;
                        margin-bottom: 2rem !important;
                    }
                    
                    .section-title, .first-section-title {
                        font-size: 1.1rem !important;
                        margin-top: 2rem !important;
                    }
                    
                    .main-content {
                        padding: 2rem 0 !important;
                    }
                    
                    .warning-box, .registration-box {
                        padding: 1rem !important;
                        font-size: 1rem !important;
                    }
                }

                @media (max-width: 640px) {
                    .page-title {
                        font-size: 1.8rem !important;
                    }
                    
                    .section-title, .first-section-title {
                        font-size: 1rem !important;
                    }
                    
                    .paragraph {
                        font-size: 0.95rem !important;
                    }
                }
            `}</style>

            <div style={styles.pageContainer}>
                {/* Header */}
                <Header />

                {/* Main Content */}
                <main style={styles.mainContent} className="main-content">
                    <div style={styles.container} className="container">
                        <h1 style={styles.pageTitle} className="page-title">
                            Disclaimer
                        </h1>

                        <div style={styles.content}>
                            {/* SEBI Standard Warning */}
                            <div style={styles.warningBox} className="warning-box">
                                Investment in securities market are subject to market risks. Read all the related documents carefully before investing.
                            </div>

                            {/* AMFI Registration Disclaimer */}
                            <div style={styles.registrationBox} className="registration-box">
                                <strong>AMFI Registration Disclaimer:</strong> WinNest is an AMFI Registered Mutual Fund Distributor (ARN-81431). We deal only in Regular Plans and earn trail commission from Asset Management Companies. Direct Plans with lower expense ratios are available to investors who can invest directly with AMCs. Mutual fund investments are subject to market risks, read all scheme related documents carefully.
                            </div>

                            <h2 style={styles.firstSectionTitle} className="first-section-title">
                                Investment Risks & Market Disclosures
                            </h2>
                            <p style={styles.paragraph}>
                                All investments in securities, mutual funds, and other financial instruments are subject to market risks and volatility. The value of investments may fluctuate due to various factors including economic conditions, market sentiment, regulatory changes, and company-specific developments. Past performance is not indicative of future results, and there can be no assurance of profits or protection against losses. Investors should carefully assess their risk tolerance and financial situation before making investment decisions.
                            </p>

                            <h2 style={styles.sectionTitle} className="section-title">
                                Regulatory Compliance
                            </h2>
                            <p style={styles.paragraph}>
                                WinNest operates as an AMFI Registered Mutual Fund Distributor (ARN-81431) and complies with all applicable regulations including SEBI (Mutual Funds) Regulations, 1996, and AMFI Code of Conduct. Our services are limited to mutual fund distribution and financial planning assistance. We do not provide investment advisory services as defined under SEBI (Investment Advisers) Regulations, 2013. Clients are advised to verify our AMFI registration status on the official AMFI website (www.amfiindia.com) before availing any services.
                            </p>

                            <h2 style={styles.sectionTitle} className="section-title">
                                Mutual Fund Distribution Services
                            </h2>
                            <p style={styles.paragraph}>
                                Our mutual fund distribution services include assistance in selecting suitable schemes based on your risk profile and financial goals. We provide information and guidance but do not guarantee the accuracy, completeness, or timeliness of any information provided. Scheme recommendations are not assurances of future performance and should not be construed as promises of returns. Investors must exercise their own judgment and read all scheme related documents carefully before making investment decisions.
                            </p>

                            <h2 style={styles.sectionTitle} className="section-title">
                                Mutual Fund Investments
                            </h2>
                            <p style={styles.paragraph}>
                                Mutual fund investments are subject to market risks, and investors should read all scheme-related documents carefully before investing. Dividend options do not guarantee dividend payments, which depend on the scheme's distributable surplus. NAV-based calculations reflect valuations as per the applicable cut-off times and may not represent real-time market values. Investors should understand the features, risks, and costs associated with mutual fund schemes before investing.
                            </p>

                            <h2 style={styles.sectionTitle} className="section-title">
                                Technology & AI Disclosure
                            </h2>
                            <p style={styles.paragraph}>
                                Our platform may utilize technology tools to enhance service delivery and provide better mutual fund selection assistance. The extent of technology usage in our services is disclosed in our client agreements. While technology assists our analysis, all mutual fund recommendations undergo human oversight and professional review by NISM certified professionals.
                            </p>

                            <h2 style={styles.sectionTitle} className="section-title">
                                Client Information & KYC Requirements
                            </h2>
                            <p style={styles.paragraph}>
                                Maintaining accurate and updated client information is mandatory for regulatory compliance. Clients must promptly update changes in personal details, bank accounts, nominee information, and contact details. Failure to maintain current information may result in delays in service delivery or regulatory non-compliance. KYC procedures are conducted as per SEBI norms and applicable regulations.
                            </p>

                            <h2 style={styles.sectionTitle} className="section-title">
                                Grievance Redressal
                            </h2>
                            <p style={styles.paragraph}>
                                In case of any grievances, clients can approach our designated grievance officer or contact SEBI through their investor grievance portal. We are committed to resolving client concerns within the timeframes specified by SEBI regulations. Clients have the right to escalate unresolved grievances to SEBI or other appropriate authorities.
                            </p>

                            <h2 style={styles.sectionTitle} className="section-title">
                                Limitation of Liability
                            </h2>
                            <p style={styles.paragraph}>
                                While we strive for accuracy in all communications and reports, we do not guarantee error-free information. We reserve the right to rectify any errors or omissions identified in our reports, statements, or communications. Our liability is limited to the extent permitted by applicable laws and regulations. We do not promise assured returns, guaranteed profits, or immunity from market losses.
                            </p>

                            <h2 style={styles.sectionTitle} className="section-title">
                                Third-Party Information & External Links
                            </h2>
                            <p style={styles.paragraph}>
                                Any third-party information, links, or references provided are for informational purposes only and do not constitute our endorsement. We are not responsible for the accuracy, reliability, or content of external websites or third-party services. Clients should exercise caution and conduct independent verification before relying on external information.
                            </p>

                            <h2 style={styles.sectionTitle} className="section-title">
                                Regulatory Disclosures
                            </h2>
                            <p style={styles.paragraph}>
                                Securities quoted or mentioned on our platform are for illustration purposes only and are not recommendatory. Any examples or case studies presented are hypothetical and not indicative of future performance. We comply with SEBI's advertising guidelines and do not make claims of being "best," "number one," or use superlative terms without factual basis. We do not offer guaranteed returns or assured income schemes.
                            </p>

                            <h2 style={styles.sectionTitle} className="section-title">
                                Important Notice for Investors
                            </h2>
                            <p style={styles.paragraph}>
                                Prevent unauthorized transactions in your account. Update your mobile numbers and email IDs with us. Receive information about your transactions directly from stock exchanges and depositories. Beware of fraudulent schemes promising guaranteed returns or unrealistic profits. Always verify the SEBI registration of financial service providers before investing. For more information on investor protection, visit the SEBI investor portal at investor.sebi.gov.in.
                            </p>

                            <div style={styles.warningBox} className="warning-box">
                                <strong>IMPORTANT:</strong> Clients should read and understand all terms and conditions, risk disclosures, and scheme-related documents before availing any investment services.
                            </div>
                        </div>
                    </div>
                </main>

                {/* Layout */}
                <Layout />

                {/* Footer */}
                <Footer />
            </div>
        </>
    );
};

export default DisclaimerPage;